import type { AllBlockNames } from '@storyblok-types';
import type { Component } from 'svelte';

const components = {
  page: () => import('../components/Page.svelte'),
  pageSection: () => import('../components/PageSection.svelte'),
  columns: () => import('../components/Columns.svelte'),
  column: () => import('../components/Column.svelte'),
  grid: () => import('../components/Grid.svelte'),
  imageCard: () => import('../components/ImageCard.svelte'),
  endorsement: () => import('../components/EndorsementCard.svelte'),
  text: () => import('../components/Text.svelte'),
  button: () => import('../components/Button.svelte'),
  buttons: () => import('../components/Buttons.svelte'),
  header: () => import('../components/Header.svelte'),
  carousel: () => import('../components/Carousel.svelte'),
  iconCard: () => import('../components/IconCard.svelte'),
  teaser: () => import('../components/Teaser.svelte'),
  trustPilotServiceReview: () => import('../components/TrustPilotServiceReview.svelte'),
  trustPilotProductReviewList: () => import('../components/TrustPilotProductReviewList.svelte'),
  video: () => import('../components/Video.svelte'),
  accordion: () => import('../components/Accordion.svelte'),
  popularCategory: () => import('../components/PopularCategory.svelte'),
  recentCategory: () => import('../components/RecentCategory.svelte'),
  designersCategory: () => import('../components/DesignersCategory.svelte'),
  material: () => import('../components/Material.svelte'),
  product: () => import('../components/Product.svelte'),
  tabs: () => import('../components/Tabs.svelte'),
  image: () => import('../components/Image.svelte'),
  usage: () => import('../components/Usage.svelte'),
  languageSpecific: () => import('../components/LanguageSpecific.svelte'),
  checkout: () => import('../components/CheckoutBlok.svelte'),
  editor: () => import('../components/Editor.svelte'),
  customer: () => import('../components/Customer.svelte'),
  layout: () => import('../components/Layout.svelte'),
  readMoreText: () => import('../components/ReadMoreText.svelte'),
  specsTable: () => import('../components/SpecsTable.svelte'),
  tableContainer: () => import('../components/TableContainerBlok.svelte'),
  figureList: () => import('../components/FigureListBlok.svelte'),
  featuredPage: () => import('../components/FeaturedPageBlok.svelte'),
  uspPageSection: () => import('../components/UspPageSection.svelte'),
  collagePageSection: () => import('../components/CollagePageSection/CollagePageSection.svelte'),
  textPageSection: () => import('../components/TextPageSection.svelte'),
  textMediaPageSection: () => import('../components/TextMediaPageSection.svelte'),
  textMediaColumnsPageSection: () => import('../components/TextMediaColumnsPageSection.svelte'),
  collageImage: () => import('../components/CollagePageSection/CollageImage.svelte'),
  tabsPageSection: () => import('../components/TabsPageSection.svelte'),
  endorsementPageSection: () => import('../components/EndorsementPageSection.svelte'),
  galleryPageSection: () => import('../components/GalleryPageSection/GalleryPageSection.svelte'),
  support: () => import('../components/Support.svelte'),
  buyStickerCard: () => import('../components/BuyStickerCardBlok.svelte'),
  imageCardsPageSection: () => import('../components/ImageCardsPageSection.svelte'),
  faqPageSection: () => import('../components/FaqPageSection.svelte'),
  referencePageSection: () => import('../components/ReferencePageSection.svelte'),
  productWizard: () => import('../components/ProductWizard.svelte'),
  animatedCarouselPageSection: () =>
    import('../components/AnimatedCarouselPageSection/AnimatedCarouselPageSection.svelte'),
  iconCardsPageSection: () => import('../components/IconCardsPageSection.svelte'),
  quoteForm: () => import('../components/QuoteForm.svelte'),
  heroPageSection: () => import('../components/HeroPageSection/HeroPageSection.svelte'),
  heroHomePageSection: () => import('../components/HeroPageSection/HeroHomePageSection.svelte'),
  collectionPageSection: () =>
    import('../components/CollectionPageSection/CollectionPageSection.svelte'),
  mediaBannerPageSection: () => import('../components/MediaBannerPageSection.svelte'),
  ctaPageSection: () => import('../components/CtaPageSection.svelte'),
  trustPilotServiceReviewPageSection: () =>
    import('../components/TrustPilotServiceReviewPageSection.svelte'),
  blogPost: () => import('../components/BlogPostBlok.svelte'),
  author: () => import('../components/AuthorBlok.svelte'),
  reorder: () => import('../components/CustomerPages/ReorderView.svelte'),
  blogCategory: () => import('../components/BlogCategoryBlok.svelte'),
  supportCategory: () => import('../components/SupportCategoryBlok.svelte'),
  mediaPageSection: () => import('../components/MediaPageSection.svelte'),
  blogLanding: () => import('../components/BlogLandingBlok.svelte'),
  blogPostInfo: () => import('../components/BlogPostInfoBlok.svelte'),
  recentBlogPostsPageSection: () => import('../components/RecentBlogPostsPageSection.svelte'),
  blogPostAuthorInfo: () => import('../components/BlogAuthorInfoBlok.svelte'),
  emptyPageSection: () => import('../components/EmptyPageSection.svelte'),
  contactUsPageSection: () => import('../components/ContactUsPageSection.svelte'),
  buyStickerCardsPageSection: () => import('../components/BuyStickerCardsPageSection.svelte'),
  campaignCodePageSection: () => import('../components/CampaignCodePageSection.svelte'),
  chatPageSection: () => import('../components/ChatPageSection.svelte'),
  orderConfirmation: () => import('../components/OrderConfirmationBlok.svelte'),
  wizardCardsPageSection: () => import('../components/WizardCardsPageSection.svelte'),
  shippingCountries: () => import('../components/ShippingCountries.svelte'),
  wizardWithMedia: () => import('../components/WizardWithMedia.svelte'),
} satisfies {
  [key in AllBlockNames]?: () => Promise<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    default: Component<any>;
  }>;
};

export async function getLoadedComponentsMap(componentNames?: string[]) {
  const loadedComponents: Record<string, unknown> = {};
  for (const [name, componentLoader] of Object.entries(components)) {
    if (!componentNames || componentNames.includes(name)) {
      loadedComponents[name] = (await componentLoader()).default;
    }
  }
  return loadedComponents as Partial<typeof components>;
}
